/**
 * Per-pane chart settings (TradingView "Chart settings" analogue, ADR-0026): chart style and its
 * colours, status line, price scale, appearance. Stored in the chart-layout pane spec
 * (`ChartPaneSpec.settings`, untrusted: always read through `sanitizeChartSettings`) and posted to
 * the engine as a whole (`ChartEngine.setChartSettings`), in the render worker or the compat
 * renderer alike.
 *
 * Colours are CSS strings; `null` means "the theme's token" (so the defaults follow the visual
 * style — Tradesea / Glacis — and light / dark), resolved per theme by `resolveLook`.
 */
import type { PriceScaleMode } from "./scale.ts";
import { parseColor, withAlpha, type ChartTheme, type RGBA } from "./theme.ts";

export type ChartStyle =
  | "candles"
  | "hollow"
  | "bars"
  | "line"
  | "lineMarkers"
  | "step"
  | "area"
  | "baseline"
  | "heikinAshi"
  | "highLow";

/** Picker order and names (TradingView's chart-type menu wording). */
export const CHART_STYLES: readonly { id: ChartStyle; name: string }[] = [
  { id: "bars", name: "Bars" },
  { id: "candles", name: "Candles" },
  { id: "hollow", name: "Hollow candles" },
  { id: "heikinAshi", name: "Heikin Ashi" },
  { id: "line", name: "Line" },
  { id: "lineMarkers", name: "Line with markers" },
  { id: "step", name: "Step line" },
  { id: "area", name: "Area" },
  { id: "baseline", name: "Baseline" },
  { id: "highLow", name: "High-low" },
];

export const SCALE_MODES: readonly { id: PriceScaleMode; name: string }[] = [
  { id: "regular", name: "Regular" },
  { id: "percent", name: "Percent" },
  { id: "indexed", name: "Indexed to 100" },
  { id: "log", name: "Logarithmic" },
];

export type CrosshairStyle = "dashed" | "solid" | "dotted";

/**
 * The volume strip under the price pane (owner request 2026-10-03): `split` stacks each bar's
 * buy-aggressor volume (buy colour, from the baseline) and sell-aggressor volume (sell colour), with
 * any volume of unknown side on top in a neutral colour (never assigned to a side); `plain` is the
 * former one-colour column by candle direction; `off` hides the strip (the price pane takes it).
 */
export type VolumeDisplay = "split" | "plain" | "off";

export const VOLUME_DISPLAYS: readonly { id: VolumeDisplay; name: string }[] = [
  { id: "split", name: "Buy/Sell split" },
  { id: "plain", name: "Plain" },
  { id: "off", name: "Off" },
];

/** A colour setting: CSS colour, or null = the theme's colour. */
export type ColorSetting = string | null;

export type ChartSettings = {
  style: ChartStyle;
  /** Candles, hollow candles and Heikin Ashi. */
  candle: {
    upBody: ColorSetting;
    downBody: ColorSetting;
    border: boolean;
    upBorder: ColorSetting;
    downBorder: ColorSetting;
    wick: boolean;
    upWick: ColorSetting;
    downWick: ColorSetting;
  };
  /** OHLC bars. */
  bars: {
    up: ColorSetting;
    down: ColorSetting;
    /** 1 px bars (TradingView "Thin bars"). */
    thin: boolean;
    /** No open tick (TradingView "HLC bars"). */
    hlc: boolean;
    /** Colour by close vs the previous close instead of close vs open. */
    prevClose: boolean;
  };
  /** Line, line with markers and step line (close). */
  line: { color: ColorSetting; width: number };
  area: { line: ColorSetting; width: number; top: ColorSetting; bottom: ColorSetting };
  baseline: {
    /** Base level, % of the price pane's height from the bottom (TradingView default 50). */
    level: number;
    width: number;
    topLine: ColorSetting;
    topFill: ColorSetting;
    bottomLine: ColorSetting;
    bottomFill: ColorSetting;
  };
  highLow: { color: ColorSetting };
  /** Volume strip under the price pane. */
  volume: { display: VolumeDisplay };
  /** Status line (the OHLC legend row and the indicator legend). */
  status: { ohlc: boolean; change: boolean; volume: boolean; indicators: boolean };
  scale: {
    mode: PriceScaleMode;
    /** Auto (fit data to screen); off = manual: the price range stays while you scroll / zoom time. */
    auto: boolean;
    invert: boolean;
    placement: "right" | "left";
    lastLabel: boolean;
    lastLine: boolean;
    countdown: boolean;
  };
  appearance: {
    background: "solid" | "gradient";
    bg: ColorSetting;
    /** Gradient bottom colour. */
    bg2: ColorSetting;
    gridV: boolean;
    gridH: boolean;
    grid: ColorSetting;
    /** Vertical line at each CME trading-day start (intraday charts). */
    sessionBreaks: boolean;
    /** Shade ETH bars (outside 08:30–15:00 CT) on intraday time charts. */
    ethShade: boolean;
    ethColor: ColorSetting;
    crosshair: CrosshairStyle;
    crosshairColor: ColorSetting;
  };
};

/** Factory settings: what a pane without stored settings (and no saved default) shows. */
export const DEFAULT_CHART_SETTINGS: ChartSettings = {
  style: "candles",
  candle: {
    upBody: null,
    downBody: null,
    border: false,
    upBorder: null,
    downBorder: null,
    wick: true,
    upWick: null,
    downWick: null,
  },
  bars: { up: null, down: null, thin: false, hlc: false, prevClose: false },
  line: { color: null, width: 2 },
  area: { line: null, width: 2, top: null, bottom: null },
  baseline: { level: 50, width: 2, topLine: null, topFill: null, bottomLine: null, bottomFill: null },
  highLow: { color: null },
  // Settings saved before the split existed carry no `volume` and so take this (split).
  volume: { display: "split" },
  status: { ohlc: true, change: true, volume: true, indicators: true },
  scale: {
    mode: "regular",
    auto: true,
    invert: false,
    placement: "right",
    lastLabel: true,
    lastLine: true,
    countdown: true,
  },
  appearance: {
    background: "solid",
    bg: null,
    bg2: null,
    gridV: true,
    gridH: true,
    grid: null,
    sessionBreaks: false,
    ethShade: false,
    ethColor: null,
    crosshair: "dashed",
    crosshairColor: null,
  },
};

// ------------------------------------------------------------------------------------ sanitising

const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB = /^rgba?\(\s*[\d.]+%?\s*[,\s]\s*[\d.]+%?\s*[,\s]\s*[\d.]+%?\s*(?:[,/]\s*[\d.]+%?\s*)?\)$/i;

/** A stored colour worth keeping (hex or rgb[a]() only; anything else = the theme colour). */
export function sanitizeColor(v: unknown): ColorSetting {
  if (typeof v !== "string" || v.length > 40) return null;
  const s = v.trim();
  return HEX.test(s) || RGB.test(s) ? s : null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fb: T): T {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : fb;
}

function num(v: unknown, lo: number, hi: number, fb: number): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fb;
}

/**
 * Untrusted (stored, synced, pasted) value → complete settings. Every field is type-checked and
 * clamped; unknown keys are dropped; anything missing takes `base` (the saved default or factory).
 */
export function sanitizeChartSettings(
  raw: unknown,
  base: ChartSettings = DEFAULT_CHART_SETTINGS,
): ChartSettings {
  const r = isRecord(raw) ? raw : {};
  const group = (k: string): Record<string, unknown> => {
    const g = r[k];
    return isRecord(g) ? g : {};
  };
  const col = (g: Record<string, unknown>, k: string, fb: ColorSetting): ColorSetting =>
    k in g ? sanitizeColor(g[k]) : fb;
  const bool = (g: Record<string, unknown>, k: string, fb: boolean): boolean =>
    typeof g[k] === "boolean" ? g[k] : fb;
  const c = group("candle");
  const b = group("bars");
  const l = group("line");
  const a = group("area");
  const bl = group("baseline");
  const hl = group("highLow");
  const vo = group("volume");
  const st = group("status");
  const sc = group("scale");
  const ap = group("appearance");
  return {
    style: oneOf(
      r["style"],
      CHART_STYLES.map((s) => s.id),
      base.style,
    ),
    candle: {
      upBody: col(c, "upBody", base.candle.upBody),
      downBody: col(c, "downBody", base.candle.downBody),
      border: bool(c, "border", base.candle.border),
      upBorder: col(c, "upBorder", base.candle.upBorder),
      downBorder: col(c, "downBorder", base.candle.downBorder),
      wick: bool(c, "wick", base.candle.wick),
      upWick: col(c, "upWick", base.candle.upWick),
      downWick: col(c, "downWick", base.candle.downWick),
    },
    bars: {
      up: col(b, "up", base.bars.up),
      down: col(b, "down", base.bars.down),
      thin: bool(b, "thin", base.bars.thin),
      hlc: bool(b, "hlc", base.bars.hlc),
      prevClose: bool(b, "prevClose", base.bars.prevClose),
    },
    line: { color: col(l, "color", base.line.color), width: num(l["width"], 1, 4, base.line.width) },
    area: {
      line: col(a, "line", base.area.line),
      width: num(a["width"], 1, 4, base.area.width),
      top: col(a, "top", base.area.top),
      bottom: col(a, "bottom", base.area.bottom),
    },
    baseline: {
      level: num(bl["level"], 0, 100, base.baseline.level),
      width: num(bl["width"], 1, 4, base.baseline.width),
      topLine: col(bl, "topLine", base.baseline.topLine),
      topFill: col(bl, "topFill", base.baseline.topFill),
      bottomLine: col(bl, "bottomLine", base.baseline.bottomLine),
      bottomFill: col(bl, "bottomFill", base.baseline.bottomFill),
    },
    highLow: { color: col(hl, "color", base.highLow.color) },
    volume: {
      display: oneOf(
        vo["display"],
        VOLUME_DISPLAYS.map((v) => v.id),
        base.volume.display,
      ),
    },
    status: {
      ohlc: bool(st, "ohlc", base.status.ohlc),
      change: bool(st, "change", base.status.change),
      volume: bool(st, "volume", base.status.volume),
      indicators: bool(st, "indicators", base.status.indicators),
    },
    scale: {
      mode: oneOf(
        sc["mode"],
        SCALE_MODES.map((m) => m.id),
        base.scale.mode,
      ),
      auto: bool(sc, "auto", base.scale.auto),
      invert: bool(sc, "invert", base.scale.invert),
      placement: oneOf(sc["placement"], ["right", "left"] as const, base.scale.placement),
      lastLabel: bool(sc, "lastLabel", base.scale.lastLabel),
      lastLine: bool(sc, "lastLine", base.scale.lastLine),
      countdown: bool(sc, "countdown", base.scale.countdown),
    },
    appearance: {
      background: oneOf(ap["background"], ["solid", "gradient"] as const, base.appearance.background),
      bg: col(ap, "bg", base.appearance.bg),
      bg2: col(ap, "bg2", base.appearance.bg2),
      gridV: bool(ap, "gridV", base.appearance.gridV),
      gridH: bool(ap, "gridH", base.appearance.gridH),
      grid: col(ap, "grid", base.appearance.grid),
      sessionBreaks: bool(ap, "sessionBreaks", base.appearance.sessionBreaks),
      ethShade: bool(ap, "ethShade", base.appearance.ethShade),
      ethColor: col(ap, "ethColor", base.appearance.ethColor),
      crosshair: oneOf(ap["crosshair"], ["dashed", "solid", "dotted"] as const, base.appearance.crosshair),
      crosshairColor: col(ap, "crosshairColor", base.appearance.crosshairColor),
    },
  };
}

/** Deep equality of two sanitised settings (stable key order: both come from `sanitizeChartSettings`). */
export function sameChartSettings(a: ChartSettings, b: ChartSettings): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ------------------------------------------------------------------------------------ resolving

/** Settings resolved against a theme: every colour an RGBA, ready for the layers. */
export type ChartLook = {
  style: ChartStyle;
  upBody: RGBA;
  downBody: RGBA;
  border: boolean;
  upBorder: RGBA;
  downBorder: RGBA;
  wick: boolean;
  upWick: RGBA;
  downWick: RGBA;
  barUp: RGBA;
  barDown: RGBA;
  thinBars: boolean;
  hlcBars: boolean;
  barsPrevClose: boolean;
  line: RGBA;
  lineWidth: number;
  areaLine: RGBA;
  areaWidth: number;
  areaTop: RGBA;
  areaBottom: RGBA;
  baseLevel: number;
  baseWidth: number;
  baseTopLine: RGBA;
  baseTopFill: RGBA;
  baseBottomLine: RGBA;
  baseBottomFill: RGBA;
  highLow: RGBA;
  volume: VolumeDisplay;
  /** Split strip: volume of unknown aggressor side (neutral, never a side colour). */
  volUnsplit: RGBA;
  status: ChartSettings["status"];
  scale: ChartSettings["scale"];
  bg: RGBA;
  /** Gradient bottom colour; null = solid background. */
  bg2: RGBA | null;
  gridV: boolean;
  gridH: boolean;
  grid: RGBA;
  sessionBreaks: boolean;
  ethShade: boolean;
  ethColor: RGBA;
  crosshair: CrosshairStyle;
  crosshairColor: RGBA;
};

/** Resolve `s` against the theme's colours (null colour = token default). */
export function resolveLook(s: ChartSettings, th: ChartTheme): ChartLook {
  const c = (v: ColorSetting, fb: RGBA): RGBA => (v === null ? fb : parseColor(v, fb));
  const upBody = c(s.candle.upBody, th.up);
  const downBody = c(s.candle.downBody, th.down);
  const ap = s.appearance;
  return {
    style: s.style,
    upBody,
    downBody,
    border: s.candle.border,
    upBorder: c(s.candle.upBorder, upBody),
    downBorder: c(s.candle.downBorder, downBody),
    wick: s.candle.wick,
    upWick: c(s.candle.upWick, upBody),
    downWick: c(s.candle.downWick, downBody),
    barUp: c(s.bars.up, th.up),
    barDown: c(s.bars.down, th.down),
    thinBars: s.bars.thin,
    hlcBars: s.bars.hlc,
    barsPrevClose: s.bars.prevClose,
    line: c(s.line.color, th.accent),
    lineWidth: s.line.width,
    areaLine: c(s.area.line, th.accent),
    areaWidth: s.area.width,
    areaTop: c(s.area.top, withAlpha(th.accent, 0.28)),
    areaBottom: c(s.area.bottom, withAlpha(th.accent, 0.02)),
    baseLevel: s.baseline.level,
    baseWidth: s.baseline.width,
    baseTopLine: c(s.baseline.topLine, th.up),
    baseTopFill: c(s.baseline.topFill, withAlpha(th.up, 0.28)),
    baseBottomLine: c(s.baseline.bottomLine, th.down),
    baseBottomFill: c(s.baseline.bottomFill, withAlpha(th.down, 0.28)),
    highLow: c(s.highLow.color, th.accent),
    volume: s.volume.display,
    volUnsplit: th.textDim,
    status: { ...s.status },
    scale: { ...s.scale },
    bg: c(ap.bg, th.bg),
    bg2: ap.background === "gradient" ? c(ap.bg2, th.panel) : null,
    gridV: ap.gridV,
    gridH: ap.gridH,
    grid: c(ap.grid, th.grid),
    sessionBreaks: ap.sessionBreaks,
    ethShade: ap.ethShade,
    ethColor: c(ap.ethColor, withAlpha(th.accent, 0.07)),
    crosshair: ap.crosshair,
    crosshairColor: c(ap.crosshairColor, withAlpha(th.text, 0.6)),
  };
}
